import { Button, Dropdown, Form, InputField, PageShell } from 'components';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, createNewClient, RootState } from 'data';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { countries } from 'helpers';

export const AddNewClient = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { createClientLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    contactPersonFirstName: '',
    contactPersonLastName: '',
    contactPersonSalutation: '',
    email: '',
    phone: '',
    website: '',
    address: {
      id: undefined,
      Street: '',
      HouseNumber: '',
      PostCode: '',
      City: '',
      Country: '',
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (
      ['Street', 'HouseNumber', 'PostCode', 'City', 'Country'].includes(name)
    ) {
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [name]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
	setSubmitted(true);
	if (formData.name == '' || 
		formData.address.Street == '' ||
		formData.address.HouseNumber == '' ||
		formData.address.PostCode == '' ||
		formData.address.City == '' ||
		formData.address.Country == '' ||
		formData.contactPersonFirstName == '' ||
		formData.contactPersonLastName == '' ||
		formData.contactPersonSalutation == '' ||
		formData.email == '' ||
		formData.phone == '') {
		return
	}
    try {
      await dispatch(createNewClient(formData)).unwrap();
      if (location.state?.fromInvoice) {
        const returnUrl = `/invoices/create-new?newClient=${encodeURIComponent(formData.name)}`;
        navigate(returnUrl, {
          state: { invoiceData: location.state.fromInvoice },
        });
      } else {
        navigate('/clients');
      }
    } catch (error) {
      console.error(t('addNewClient.toast.errorCreatingClient'), error);
    }
  };

  const handleCancel = () => {
    if (location.state?.fromInvoice) {
      navigate('/invoices/create-new', {
        state: { invoiceData: location.state.fromInvoice },
      });
    } else {
      setFormData({
        name: '',
        address: {
          id: undefined,
          Street: '',
          HouseNumber: '',
          PostCode: '',
          City: '',
          Country: '',
        },
        contactPersonFirstName: '',
		contactPersonLastName: '',
		contactPersonSalutation: '',
        email: '',
        phone: '',
        website: '',
      });
      navigate('/clients');
    }
  };

	const nationalityOptions = [
		{ label: t('personalIdentification.select'), value: '' },
			...countries.map((country) => ({
			label: t(country.labelKey),
			value: country.value,
		})),
	];

  	const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

  return (
    <PageShell width="narrow">
      <h1 className="text-2xl lg:text-3xl font-medium text-[#2E435F]">
        {t('addNewClient.addNewClient')}
      </h1>
      <Form
        content={
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <InputField
              label={t('addNewClient.companyName')}
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('addNewClient.enterCompanyName')}
			  error={submitted && !formData.name ? 'Required': ''}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <InputField
                label={t('addNewClient.street')}
                name="Street"
                value={formData.address.Street}
                onChange={handleChange}
                placeholder={t('addNewClient.enterStreet')}
				error={submitted && !formData.address.Street ? 'Required': ''}
              />
              <InputField
                label={t('addNewClient.houseNumber')}
                name="HouseNumber"
                value={formData.address.HouseNumber}
                onChange={handleChange}
                placeholder={t('addNewClient.enterHouseNumber')}
                type="text"
				error={submitted && !formData.address.HouseNumber ? 'Required': ''}
              />
            </div>
            <InputField
              label={t('addNewClient.postcode')}
              name="PostCode"
              value={formData.address.PostCode}
              onChange={handleChange}
              placeholder={t('addNewClient.enterPostcode')}
              type="text"
			  error={submitted && !formData.address.PostCode ? 'Required': ''}
            />
            <InputField
              label={t('addNewClient.city')}
              name="City"
              value={formData.address.City}
              onChange={handleChange}
              placeholder={t('addNewClient.enterCity')}
			  error={submitted && !formData.address.City ? 'Required': ''}
            />
            <Dropdown
              label={t('addNewClient.country')}
              name="Country"
              value={formData.address.Country}
              options={nationalityOptions}
              onChange={handleChange}
			  error={submitted && !formData.address.Country ? 'Required': ''}
            />
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
				<Dropdown
					label={t('addNewClient.contactPersonSalutation')}
					name="contactPersonSalutation"
					value={formData.contactPersonSalutation || ''}
					options={[
						{ label: t('personalIdentification.select'), value: '' },
						{
							label: t('personalIdentification.mr'),
							value: 'mr',
						},
						{
							label: t('personalIdentification.mrs'),
							value: 'mrs',
						}
					]}
					onChange={handleDropdownChange}
					error={submitted && !formData.contactPersonSalutation ? 'Required': ''}
				/>
				<InputField
					label={t('addNewClient.contactPersonFirstName')}
					name="contactPersonFirstName"
					value={formData.contactPersonFirstName}
					onChange={handleChange}
					placeholder={t('addNewClient.contactPersonFirstName')}
					error={submitted && !formData.contactPersonFirstName ? 'Required': ''}
				/>
				<InputField
					label={t('addNewClient.contactPersonLastName')}
					name="contactPersonLastName"
					value={formData.contactPersonLastName}
					onChange={handleChange}
					placeholder={t('addNewClient.contactPersonLastName')}
					error={submitted && !formData.contactPersonLastName ? 'Required': ''}
				/>
			</div>
            <InputField
              label={t('addNewClient.email')}
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('addNewClient.enterEmailAddress')}
              type="email"
			  error={submitted && !formData.email ? 'Required': ''}
            />
            <InputField
              label={t('addNewClient.phoneNumber')}
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('addNewClient.enterPhoneNumber')}
              type="tel"
			  error={submitted && !formData.phone ? 'Required': ''}
            />
            <InputField
              label={t('addNewClient.website')}
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder={t('addNewClient.enterWebsiteUrl')}
              type="url"
            />
            <div className="flex flex-col-reverse sm:flex-row gap-4 mt-6 sm:justify-between">
              <Button
                label={t('addNewClient.cancel')}
                onClick={handleCancel}
                color="#828E9F"
                fullWidth
              />
              <Button
                label={
                  createClientLoading
                    ? t('addNewClient.loading')
                    : t('addNewClient.createClient')
                }
                type="submit"
                fullWidth
                disabled={createClientLoading}
              />
            </div>
          </form>
        }
      />
    </PageShell>
  );
};
